import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { getToken, deleteToken } from 'firebase/messaging';
import { messaging, updateFcmToken, enableNotifications } from './firebaseService';

// @ts-ignore
const VAPID_KEY = import.meta.env?.VITE_VAPID_KEY as string;

/** ID for the default Android notification channel (required Android 8+) */
export const FCM_CHANNEL_ID = 'fricamp_default';

let registrationListenerHandle: Awaited<ReturnType<typeof PushNotifications.addListener>> | null = null;
let registrationErrorListenerHandle: Awaited<ReturnType<typeof PushNotifications.addListener>> | null = null;

async function clearNativeRegistrationListeners(): Promise<void> {
  await registrationListenerHandle?.remove().catch(() => {});
  await registrationErrorListenerHandle?.remove().catch(() => {});
  registrationListenerHandle = null;
  registrationErrorListenerHandle = null;
}

/**
 * Ensures the Android notification channel exists.
 * Safe to call multiple times — channel creation is idempotent.
 */
async function ensureAndroidChannel(): Promise<void> {
  try {
    await PushNotifications.createChannel({
      id: FCM_CHANNEL_ID,
      name: 'Fricamp-varsler',
      description: 'Varsler fra Fricamp om plasser, invitasjoner og aktivitet',
      importance: 4,    // IMPORTANCE_HIGH — vises som heads-up
      sound: 'default',
      vibration: true,
      visibility: 1,    // VISIBILITY_PUBLIC
    });
  } catch (err) {
    console.warn('Could not create notification channel:', err);
  }
}

// ─── Native (Android/iOS via Capacitor) ──────────────────────────────────────

async function requestNativePermission(uid: string): Promise<boolean> {
  try {
    await ensureAndroidChannel();
    await clearNativeRegistrationListeners();

    const result = await PushNotifications.requestPermissions();
    if (result.receive !== 'granted') return false;

    await PushNotifications.register();

    return new Promise<boolean>(async (resolve) => {
      const timeout = setTimeout(() => resolve(false), 10_000);

      registrationListenerHandle = await PushNotifications.addListener('registration', async (token) => {
        clearTimeout(timeout);
        try {
          await updateFcmToken(uid, token.value);
          await enableNotifications(uid, true);
          resolve(true);
        } catch {
          resolve(false);
        } finally {
          await clearNativeRegistrationListeners();
        }
      });

      registrationErrorListenerHandle = await PushNotifications.addListener('registrationError', async () => {
        clearTimeout(timeout);
        resolve(false);
        await clearNativeRegistrationListeners();
      });
    });
  } catch (err) {
    console.error('Native push registration error:', err);
    return false;
  }
}

async function disableNativeNotifications(uid: string): Promise<void> {
  try {
    await clearNativeRegistrationListeners();
  } catch {
    // Plugin may not be registered yet — ignore
  }
  await updateFcmToken(uid, null);
  await enableNotifications(uid, false);
}

// ─── Web / PWA ────────────────────────────────────────────────────────────────

async function requestWebPermission(uid: string): Promise<boolean> {
  if (!('Notification' in window)) return false;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  // Always save the user's preference immediately after they grant permission.
  // FCM token is best-effort — missing it only means background push won't work,
  // but the user's opt-in preference must be recorded regardless.
  await enableNotifications(uid, true);

  if (messaging) {
    try {
      const swReg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swReg,
      });
      if (token) {
        await updateFcmToken(uid, token);
      }
    } catch (err) {
      console.warn('FCM web token error (non-fatal):', err);
    }
  }

  return true;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export async function requestNotificationPermission(uid: string): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    return requestNativePermission(uid);
  }
  return requestWebPermission(uid);
}

export async function disableNotifications(uid: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await disableNativeNotifications(uid);
    return;
  }
  if (messaging) {
    try { await deleteToken(messaging); } catch (_) { }
  }
  await updateFcmToken(uid, null);
  await enableNotifications(uid, false);
}

/**
 * Returns the notification permission state for UI rendering.
 * On native platforms, always returns 'default' (toggle is always shown —
 * actual enabled state is tracked via user.notificationsEnabled).
 */
export function getNotificationPermissionState(): 'granted' | 'denied' | 'default' | 'unsupported' {
  if (Capacitor.isNativePlatform()) return 'default';
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Checks if the app has OS-level permission to show notifications.
 * returns true if granted, false otherwise.
 */
export async function checkNotificationPermission(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    try {
      const result = await PushNotifications.checkPermissions();
      return result.receive === 'granted';
    } catch {
      return false;
    }
  }
  return 'Notification' in window && Notification.permission === 'granted';
}
